import { Moment } from 'moment';
import { createAppSlice } from '.';
import {
  addArticlesToTask,
  addParcelToTask,
  addTaskMetadataResponses,
  deleteTask as deleteApiTask,
  duplicateAnonymousParcelInTask,
  getTask,
  getTaskFile,
  getTasks,
  patchArticleInTask,
  patchParcelInTask,
  patchTask,
  postProductUsageInTask,
  removeArticleFromTask,
  removeParcelFromTask,
  removeProductUsageFromTask,
  sendPDFTaskFile,
} from '../api/api';
import { PHYTO_VIRTUAL_ARTICLE_ID } from '../constants/articleConstants';
import { ProductState, UsageState } from '../constants/phytosConstants';
import {
  TASK_STATUS,
  TaskMetadataQuestionFormat,
  TaskMetadataQuestionType,
  TaskPDFMapBackground,
  TaskStatus,
} from '../constants/taskConstants';
import type { RootState } from '../store';
import { objectMap } from '../utils';
import { ANALYTICS, sendAnalyticsBtnClickEvent } from '../utils/AnalyticsConstants';
import { stringifyFilters } from '../utils/filters';
import { ItemWithPermissions } from '../utils/permissions';
import { transformTaskToManyClients } from '../utils/tasks';
import { Article, ArticleId } from './articles';
import { ClusterId } from './clusters';
import { CropId } from './crops';
import { EquipmentId, EquipmentModelId, NormalizedEquipment } from './equipments';
import { observationsArchived } from './observations/archived';
import { ObservationId } from './observations/general';
import { ParcelId } from './parcels';
import { ProductId, ProductUsageId } from './phyto';
import { DeviceSessionId, EquipmentSessionId } from './sessions';
import { alerting, success } from './snacks';
import { TaskType, TaskTypeId, selectPhytoTaskTypes } from './taskTypes';
import { UserId } from './users';
import { WorkOrder, WorkOrderId, fetchWorkOrders } from './workOrders';

export type TaskId = string;
export type TaskMetadataId = string;

export type TaskMetadata = {
  id?: TaskMetadataId;
  task_type?: string;
  task_type_id?: TaskTypeId;
  readable_id?: string;
  title: string;
  response_format: TaskMetadataQuestionFormat;
  response_type: TaskMetadataQuestionType;
  defined_responses?: string[];
  group_by?: string;
  /**
   * Default responses that have to be applied for every task status, except if
   * `default_responses_apply_with_task_status` is set
   */
  default_responses?: string[];
  default_responses_apply_with_task_status?: TaskStatus[];
  responses?: string[];
  mandatory: boolean;
  is_instruction?: boolean;
  position: number;
};

export type TaskProductUsage = {
  product_usage_id: ProductUsageId;
  dose: number;
  dose_unit: string;
  default_dose: number;
  is_customized: boolean;
  total_quantity: number;
  ratio: number;
  total_unit: string;
  batch_number?: string;
  product_name: string;
  recolt_delay?: number;
  is_custom_product: boolean;
  product_functions?: string[];
  function?: string;
  product_reentry_delays?: string[];
  product_id?: ProductId;
  product_state?: ProductState;
  product_usage_state?: UsageState;
  position?: number;
  /** Readable usage of the product */
  usage_id?: string;
  znt_aquatique?: number;
  amm?: string;
};

type TaskParcelId = string;
type TaskParcelDetails = {
  article_id: ArticleId;
  goal_value?: number;
  worked_value?: number;
  computed_worked_value?: number;
};
export type TaskParcel = {
  id: TaskParcelId;
  task_id: TaskId;
  parcel_id: ParcelId;
  parcel_task_id?: TaskParcelId;
  client_cluster_id?: ClusterId;
  /** Start date of the work in the parcel for this task (RFC 3339) */
  completion_date_start?: string;
  /** End date of the work in the parcel for this task (RFC 3339) */
  completion_date_end?: string;
  /** `from_date` to use to create the anon parcel (RFC 3339) */
  from_date?: string;
  /** Whether the parcel should be created in the name of the client (defaults to `true`) */
  duplicate_anonymous_parcel?: boolean;
  position?: number;
  parcel_area: number;
  worked_area?: number;
  goal_area?: number;
  name: string;
  geometry_area: string;
  track?: string;
  product_usages?: TaskProductUsage[];
  device_session_ids?: DeviceSessionId[];
  details?: TaskParcelDetails[];

  /** @deprecated Use completion_date_start and completion_date_end */
  completion_date?: string;
  /** @deprecated Use completion_date_start and completion_date_end */
  to_date?: string;
  /** @deprecated Use device_session_ids */
  device_session_id?: DeviceSessionId;
};

export type TaskWaterData = {
  total_dose: number;
  total_quantity: number;
};

export type TaskAssignedUser = {
  user_id: UserId;
  email_address: string;
  fullname: string;
};

export type ArticleRange =
  | {
      range_type: 'float';
      lower?: number;
      upper?: number;
    }
  | {
      range_type: 'datetime';
      lower?: string;
      upper?: string;
    };
export type TaskArticle = Article & {
  values?: {
    client_cluster_id: ClusterId;
    goal_value?: number;
    worked_value?: number;
    range_value?: ArticleRange[];
    computed_worked_value?: number;
  }[];
  /** Computed totals, or values for single-client tasks coming from the API */
  goal_value?: number;
  worked_value?: number;
  range_value?: ArticleRange[];
  computed_worked_value?: number;

  /** @deprecated Use goal and worked values */
  value?: number;
};

type PDFColorChoice = '' | 'equipment' | 'cluster' | 'crop' | 'tag';

export type TaskPDFConfig = Partial<{
  with_instruction: boolean;
  with_observation: boolean;
  with_parcel_session_track_images: boolean;
  with_equipment_session_track_images: boolean;
  with_parcel_details: boolean;
  map_type: TaskPDFMapBackground;
  map_track_color_by: PDFColorChoice;
  map_parcel_fill_color_by: PDFColorChoice;
}>;

export type BaseTask = ItemWithPermissions & {
  articles: TaskArticle[];
  assigned_to_ids?: UserId[];
  assigned_users?: TaskAssignedUser[];
  client_cluster_ids: ClusterId[];
  created_at: string;
  crop_range_id?: CropId;
  device_session_ids?: DeviceSessionId[];
  equipment_instance_ids?: EquipmentId[];
  equipments?: NormalizedEquipment[];
  from_date?: string;
  fromDate?: Moment;
  metadata: TaskMetadata[];
  name?: string;
  observation_ids?: ObservationId[];
  owner_cluster_id: ClusterId;
  owner_name: string;
  parcels?: TaskParcel[];
  pdf_config?: TaskPDFConfig;
  product_usages?: TaskProductUsage[];
  scoring?: number;
  sortDate?: string;
  status: TaskStatus;
  task_type_id?: TaskTypeId;
  taskType?: TaskType;
  type?: string;
  to_date?: string;
  toDate?: Moment;
  total_dose?: number;
  total_quantity?: number;
  water_data?: TaskWaterData;
  track?: string;
};

export type Task = BaseTask & {
  id: TaskId;
  is_work_order?: false;
  client_cluster_id: ClusterId;
  client_name: string;
  completion_date?: string;
  equipment_session_ids?: EquipmentSessionId[];
  phyto_file_generated: boolean;
  bdl_file_generated: boolean;
  mail_count?: number;
  work_order_ids?: WorkOrderId[];

  /** @deprecated Use `equipment_instance_ids` */
  equipment_instance_id?: EquipmentId;
  /** @deprecated */
  equipment_model_id: EquipmentModelId;
  /** @deprecated Use `device_session_ids` */
  device_session_id?: DeviceSessionId;
  /** @deprecated Use `assigned_to_ids` */
  assigned_to_id?: UserId;
  /** @deprecated Use `assigned_to_users` */
  assigned_to_fullname?: string;
  /** @deprecated Use `assigned_to_users` */
  assigned_to_email?: string;
  /** @deprecated Use `equipment_instance_ids` */
};

export type AnyTask = Task | WorkOrder;

type TaskFilters = {
  client_cluster_id?: ClusterId[];
  crop_range_id?: CropId[];
  equipment_instance_id?: EquipmentId[];
  from_completion_date?: string;
  to_completion_date?: string;
  status?: TaskStatus | TaskStatus[];
  task_type_id?: TaskTypeId[];
  user_id?: UserId[];
  parcel_id?: ParcelId[];
};

type TaskReport = {
  sum_computed_value: number;
  sum_goal_value: number;
  sum_worked_value: number;
  task_ids: TaskId[];
  tasks: Task[];
};

export type TaskClientReport = {
  [articleId: ArticleId]: TaskReport;
};

export type TaskArticleReport = {
  [clusterId: ClusterId]: TaskReport;
};

type State = {
  tasks: Task[];
  loading: boolean;
  lastFetched: Task | null;
};

const initialState: State = {
  tasks: [],
  loading: true,
  lastFetched: null,
};

const tasksCache = new Map<string, Task[]>();

function areFiltersCached(filters: TaskFilters) {
  const key = stringifyFilters(filters);
  return tasksCache.has(key) ? key : null;
}

function receiveTask(state: State, task: Task) {
  state.lastFetched = task;
  state.tasks = state.tasks.filter((t) => t.id !== task.id);
  state.tasks.push(task);
}

const tasksSlice = createAppSlice({
  name: 'tasks',
  initialState,
  reducers: (create) => ({
    fetchTasks: create.asyncThunk(
      async (filters: TaskFilters, { signal, dispatch }) => {
        const tasks = (await alerting(() => getTasks(filters, signal), { dispatch })) as Task[] | undefined;
        if (tasks) {
          const key = stringifyFilters(filters);
          tasksCache.set(key, tasks);
        }
        return tasks || [];
      },
      {
        pending: (state, { meta: { arg } }) => {
          const key = areFiltersCached(arg);
          if (key) {
            // immediately load cached tasks without pending state
            state.tasks = tasksCache.get(key)!;
          } else {
            state.loading = true;
          }
        },
        fulfilled: (state, { payload }) => {
          state.tasks = payload;
        },
        settled: (state, { meta }) => {
          // if the request was aborted, another one is coming right after so we don’t hide
          // the loader until the next request completes (or fails)
          if (meta.requestStatus !== 'rejected' || !meta.aborted) {
            state.loading = false;
          }
        },
      },
    ),
    fetchTask: create.asyncThunk(
      async (taskId: TaskId, { dispatch }) => {
        const task = (await alerting(() => getTask(taskId), { dispatch })) as Task;
        return task;
      },
      {
        fulfilled: (state, { payload }) => {
          receiveTask(state, payload);
        },
      },
    ),
    updateTask: create.asyncThunk(
      async (
        { previousTask, task, withSnack = true }: { previousTask: Task; task: Partial<Task>; withSnack?: boolean },
        { dispatch, getState },
      ) => {
        const promises: Promise<unknown>[] = [];
        if (task.parcels) {
          const deletedParcels = (previousTask.parcels || []).filter(
            (pp) => !task.parcels?.find((p) => pp.parcel_id === p.parcel_id),
          );
          for (const parcel of deletedParcels) {
            promises.push(removeParcelFromTask(previousTask.id, parcel.parcel_id));
          }

          const addedParcels = task.parcels.filter(
            (p) => !(previousTask.parcels || []).find((pp) => p.parcel_id === pp.parcel_id),
          );
          for (const parcel of addedParcels) {
            promises.push(addParcelToTask(previousTask.id, parcel.parcel_id, parcel));
          }

          const updatedParcels = task.parcels.filter((p) => !addedParcels.find((ap) => p.parcel_id === ap.parcel_id));
          for (const parcel of updatedParcels) {
            const previousParcel = (previousTask.parcels || []).find((pp) => pp.parcel_id === parcel.parcel_id);
            const previousGoalArea = previousParcel?.goal_area;
            promises.push(
              patchParcelInTask(previousTask.id, parcel.parcel_id, {
                ...parcel,
                goal_area: (task.status === TASK_STATUS.TO_DO ? parcel.goal_area : previousGoalArea) ?? null,
                worked_area: parcel.worked_area ?? null,
                details: (parcel.details || []).map((d) => {
                  const previousGoalValue = previousParcel?.details?.find(
                    (pd) => pd.article_id === d.article_id,
                  )?.goal_value;
                  return {
                    ...d,
                    goal_value: (task.status === TASK_STATUS.TO_DO ? d.goal_value : previousGoalValue) ?? null,
                    worked_value: d.worked_value ?? null,
                  };
                }),
              }),
            );
          }
        }

        if (task.metadata) {
          const responses = task.metadata
            .filter((m) => !!m.id && !!m.responses?.length)
            .map((m) => ({ task_metadata_id: m.id!, responses: m.responses }));
          promises.push(addTaskMetadataResponses(previousTask.id, responses));
        } else {
          const phytoTaskTypes = selectPhytoTaskTypes(getState() as RootState);
          const taskType = phytoTaskTypes.find((t) => t.id === previousTask.task_type_id);
          if (taskType && task.status && task.status !== previousTask.status) {
            // set default metadata
            const newMetadata = (taskType?.metadata || [])
              .map((m) => {
                const currentResponses = (previousTask.metadata.find((tm) => tm.id === m.id)?.responses || []).filter(
                  (r) => String(r).length,
                );
                // if no response saved, and default responses apply with new status, use default responses
                if (
                  !currentResponses.length &&
                  m.default_responses &&
                  (!m.default_responses_apply_with_task_status ||
                    m.default_responses_apply_with_task_status.includes(task.status!))
                ) {
                  return { task_metadata_id: m.id, responses: m.default_responses };
                }
                return { task_metadata_id: m.id, responses: currentResponses };
              })
              .filter((m) => !!m.task_metadata_id && !!m.responses?.length);
            promises.push(addTaskMetadataResponses(previousTask.id, newMetadata));
          }
        }

        if (task.articles) {
          const previousArticles = (previousTask.articles || []).filter((a) => a.id !== PHYTO_VIRTUAL_ARTICLE_ID);
          const newArticles = task.articles.filter((a) => a.id !== PHYTO_VIRTUAL_ARTICLE_ID);
          const deletedArticles = previousArticles.filter((pa) => !newArticles.find((a) => pa.id === a.id));
          for (const article of deletedArticles) {
            promises.push(removeArticleFromTask(previousTask.id, article.id));
          }

          const addedArticles = newArticles
            .filter((a) => !previousArticles.find((pa) => a.id === pa.id))
            .map((a) => {
              const values = a.values?.[0];
              return {
                article_id: a.id,
                position: a.position,
                goal_value: values?.goal_value ?? null,
                worked_value: values?.worked_value ?? null,
                range_value: values?.range_value ?? [],
              };
            });
          if (addedArticles.length > 0) {
            promises.push(addArticlesToTask(previousTask.id, addedArticles));
          }

          const updatedArticles = task.articles.filter((a) => !addedArticles.find((aa) => aa.article_id === a.id));
          for (const article of updatedArticles) {
            const values = article.values?.[0];
            const previousGoalValue = previousArticles.find((pa) => pa.id === article.id)?.values?.[0]?.goal_value;
            promises.push(
              patchArticleInTask(previousTask.id, article.id, {
                worked_value: values?.worked_value ?? null,
                range_value: values?.range_value ?? [],
                goal_value: (task.status === TASK_STATUS.TO_DO ? values?.goal_value : previousGoalValue) ?? null,
                position: article.position,
              }),
            );
          }
        }

        const updatedTask = await alerting(
          async () => {
            // update parcels, articles and metadata in parallel
            await Promise.all(promises);

            // create parcels from anonymous parcels
            if (task.parcels) {
              await Promise.all(
                task.parcels
                  .filter((p) => p.duplicate_anonymous_parcel)
                  .map((p) => previousTask.parcels?.find((pp) => pp.parcel_id === p.parcel_id))
                  .filter((p) => !!p && !!p.parcel_task_id)
                  .map((p) => duplicateAnonymousParcelInTask(previousTask.id, p!.parcel_task_id!)),
              );
            }

            // update task
            let updatedTask = (await patchTask(
              previousTask.id,
              objectMap(task, (v, k) => (k === 'parcels' || k === 'metadata' || k === 'articles' ? undefined : v)),
            )) as Task;

            // for dose calculation, we need to update product doses sequentially (because the last state depends on the previous updates)
            if (task.product_usages) {
              // remove product usages that have been removed from the task
              const usagesToDelete =
                previousTask.product_usages?.filter(
                  (pu) => !task.product_usages?.find((wpu) => wpu.product_usage_id === pu.product_usage_id),
                ) || [];
              for (const previousUsage of usagesToDelete) {
                updatedTask = await removeProductUsageFromTask(previousTask.id, previousUsage.product_usage_id);
              }
              // add or update the usages that have been added or updated
              const usagesToUpsert =
                task.product_usages?.filter((pu) => {
                  const productInPrevious = previousTask.product_usages?.find(
                    (wpu) => wpu.product_usage_id === pu.product_usage_id,
                  );
                  return (
                    !productInPrevious ||
                    pu.dose !== productInPrevious.dose ||
                    pu.batch_number !== productInPrevious.batch_number ||
                    pu.position !== productInPrevious.position ||
                    pu.function !== productInPrevious.function
                  );
                }) || [];
              for (const usage of usagesToUpsert) {
                updatedTask = await postProductUsageInTask(previousTask.id, usage.product_usage_id, usage);
              }
            }
            sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.TASKS.EDIT_TASK);

            return updatedTask;
          },
          { dispatch },
        );

        if (withSnack) {
          success('Tasks.update_snack', { dispatch });
        }

        if (task.status === TASK_STATUS.ARCHIVED && previousTask.status !== TASK_STATUS.ARCHIVED) {
          observationsArchived({ taskIds: [previousTask.id] }, { dispatch });
        }

        return updatedTask;
      },
      {
        fulfilled: (state, { payload }) => {
          receiveTask(state, payload);
        },
      },
    ),
    deleteTask: create.asyncThunk(
      async (taskId: TaskId, { dispatch }) => {
        await alerting(() => deleteApiTask(taskId), { dispatch });
        success('Tasks.delete_snack', { dispatch });
        sendAnalyticsBtnClickEvent(window)(ANALYTICS.LABEL.TASKS.DELETE_TASK);
      },
      {
        fulfilled: (state, { meta: { arg } }) => {
          state.tasks = state.tasks.filter((task) => task.id !== arg);
        },
      },
    ),
    fetchTaskFile: create.asyncThunk(
      async ({ taskId, phyto = false }: { taskId: TaskId; phyto?: boolean }, { dispatch }) => {
        await alerting(() => getTaskFile(taskId, { phyto }), { dispatch });
        sendAnalyticsBtnClickEvent(window)(
          phyto ? ANALYTICS.LABEL.WORK_ORDERS.PDF_PHYTO_GENERATED : ANALYTICS.LABEL.WORK_ORDERS.PDF_TASK_GENERATED,
        );
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { taskId, phyto = false },
            },
          },
        ) => {
          state.tasks = state.tasks.map((task) =>
            task.id === taskId ? { ...task, [phyto ? 'phyto_file_generated' : 'bdl_file_generated']: true } : task,
          );
        },
      },
    ),
    sendTaskFile: create.asyncThunk(
      async (payload: { taskId: TaskId; email: string; phyto?: boolean }, { dispatch }) => {
        const phyto = payload.phyto ?? true;
        await alerting(() => sendPDFTaskFile(payload.taskId, { phyto }), { dispatch });
        success('Tasks.send_pdf_snack', { params: { email: payload.email }, dispatch });
        return payload.taskId;
      },
      {
        fulfilled: (state, { payload }) => {
          state.tasks = state.tasks.map((task) =>
            task.id === payload ? { ...task, mail_count: (task.mail_count || 0) + 1 } : task,
          );
        },
      },
    ),
  }),
  extraReducers: (builder) => {
    builder.addCase(fetchWorkOrders.pending, (state, { meta }) => {
      const key = areFiltersCached(meta.arg);
      if (key) {
        // immediately load cached work orders without pending state
        state.tasks = tasksCache.get(key)!;
      } else {
        state.loading = true;
      }
    });
    builder.addCase(fetchWorkOrders.fulfilled, (state, { payload, meta }) => {
      const tasks = payload
        .map((workOrder) => workOrder.tasks || [])
        .flat()
        .map(transformTaskToManyClients);
      const key = stringifyFilters(meta.arg);
      tasksCache.set(key, tasks);
      state.tasks = tasks;
      state.loading = false;
    });
  },
  selectors: {
    selectTasksLoading: (state) => state.loading,
    selectTasks: (state) => state.tasks,
    selectLastFetchedTask: (state) => state.lastFetched,
  },
});

export const { fetchTasks, fetchTask, updateTask, deleteTask, fetchTaskFile, sendTaskFile } = tasksSlice.actions;
export const { selectLastFetchedTask, selectTasks, selectTasksLoading } = tasksSlice.selectors;

export default tasksSlice.reducer;
